import api, { V2 } from './axios';

/** One path's copy for a service: short card + long detail + symptom bullets. */
export interface ServiceDescription {
  card?: string;
  detail?: string;
  bullets?: string[];
}
/** Per-service descriptions keyed by catalog slug, split by path (a = pain, b = wellness). */
export type ServiceDescriptions = Record<string, { a?: ServiceDescription; b?: ServiceDescription }>;

/* ---- Partner ("/partners") landing page content (Mongo-driven) ---- */
export interface PartnerBenefit {
  icon: string;
  title: string;
  body: string;
  tag?: string;
  featured?: boolean;
}
export interface PartnerSegment {
  emoji: string;
  color: string;
  title: string;
  body: string;
}
export interface PartnerStat {
  value: string;
  unit?: string;
  label: string;
  source?: string;
}
export interface PartnerTestimonial {
  text: string;
  stat?: string;
  name: string;
  title: string;
  initials: string;
}
export interface PartnerFaq {
  q: string;
  a: string;
}
export interface PartnerLanding {
  hero: {
    eyebrow: string;
    title: string;
    title_highlight: string;
    subtitle: string;
    primary_cta: string;
    secondary_cta: string;
    trust_items: string[];
    image_url: string;
    image_label?: string;
    stat_chips: { value: string; label: string }[];
  };
  stats: { label: string; title: string; subtitle: string; items: PartnerStat[] };
  benefits: { label: string; title: string; subtitle: string; items: PartnerBenefit[] };
  home_visit: { label: string; title: string; body: string; cta: string };
  segments: { label: string; title: string; subtitle: string; image_url: string; items: PartnerSegment[] };
  steps: { label: string; title: string; subtitle: string; items: { title: string; body: string }[] };
  testimonials: { label: string; title: string; items: PartnerTestimonial[] };
  trust_badges: { icon: string; label: string }[];
  faqs: { label: string; title: string; items: PartnerFaq[] };
  final_cta: { title: string; subtitle: string; note: string; whatsapp?: string };
  aggregates?: { partner_count: number; cities_covered: number; sessions_booked: number };
}

export const configApi = {
  getSiteConfig: () =>
    api.get(`${V2}config/site/`),

  getStats: () =>
    api.get(`${V2}config/stats/`),

  getTherapyOptions: (serviceName?: string) =>
    serviceName
      ? api.get(`${V2}config/therapy-options/${encodeURIComponent(serviceName)}/`)
      : api.get(`${V2}config/therapy-options/`),

  getCentres: () =>
    api.get(`${V2}centres/`),

  submitCityRequest: (data: { city: string; pincode: string }) =>
    api.post(`${V2}config/city-request/`, data),

  getTopReviews: () =>
    api.get(`${V2}config/top-reviews/`),

  getLegalDocs: () =>
    api.get<{ documents: { id: string; label: string; order: number; content: string }[] }>(
      `${V2}config/legal/`,
    ),

  getServiceDescriptions: () =>
    api.get<{ descriptions: ServiceDescriptions }>(`${V2}config/service-descriptions/`),

  getPartnerLanding: () =>
    api.get<{ landing: PartnerLanding }>(`${V2}config/partner-landing/`),

  trackClick: (data: { link_type: string; target_url: string }) =>
    api.post(`${V2}config/track-click/`, data),

  getSurveyStatus: () =>
    api.get(`${V2}config/survey-response/`),

  submitSurveyResponse: (responses: Record<string, string | string[]>) =>
    api.post(`${V2}config/survey-response/`, { responses }),

  completeSurvey: () =>
    api.post(`${V2}config/survey-complete/`, {}),
};
